import { Component } from "react"

/* Components */
import { Input } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Switch */
import Switch from "react-switch"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { saveGame, toggleGame } from "../../api/Rsg"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, InfoIcon, KeyIcon, TableIcon, SendIcon } from "./RsgIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY MODAL STATE */
const EMPTY = {
    tableID: 0,
    name: "",
    slug: "",
    studio: "",
    gameID: "",
    savedGameID: "",
    isActive: true,
    savedActive: true,
    waiting: false,
}

/* GAME ID OF THE REGULATOR — LETTERS, NUMBERS, A DASH, AN UNDERSCORE AND A DOT */
const GAME_ID_PATTERN = /^[A-Za-z0-9._-]+$/

/* LENGTH OF THE GAME ID COLUMN */
const GAME_ID_LENGTH = 100


/* Widget Rsg Game Form */
class RsgGameForm extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action — every value of the row is already loaded by the page */
    open = ({ item }) => {

        /* Fields */
        const gameID = utils.checkValue(item.rsGameID, "")
        const active = gameID === "" ? true : parseInt(item.isActive) === 1

        this.setState({
            visible: true,
            ...EMPTY,
            tableID: parseInt(item.tableID),
            name: utils.checkValue(item.tableName, ""),
            slug: utils.checkValue(item.tableSlug, ""),
            studio: utils.checkValue(item.studioName, ""),
            gameID,
            savedGameID: gameID,
            isActive: active,
            savedActive: active,
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { tableID, gameID, savedGameID, isActive, savedActive } = this.state
        const { reload } = this.props
        const value = gameID.trim()

        /* Check Game ID Format */
        if (value !== "" && !GAME_ID_PATTERN.test(value)) {
            this._gameID.error(t("rsg.error.gameIDFormat"))
            return
        }

        /* Check Changes */
        if (value === savedGameID && isActive === savedActive) {
            this.close()
            return
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* Send to REST API */
        saveGame({ tableID, rsGameID: value }).then((response) => {

            /* Check Response */
            if (response.status !== 200) {
                Swal.fire(utils.notification(this.message(response), "error"))
                this.setState({ waiting: false })
                return
            }

            /* Empty Game ID — the mapping is gone, there is nothing left to switch */
            if (value === "") {
                this.finish(t("rsg.gameIDRemoved"))
                return
            }

            /* Status After Saving — a mapping created right now is switched on */
            const current = savedGameID === "" ? true : savedActive

            /* Check Status */
            if (current === isActive) {
                this.finish(t("rsg.gameIDSaved"))
                return
            }

            /* Change Status */
            toggleGame({ tableID, value: isActive ? 1 : 0 }).then((answer) => {
                if (answer.status === 200) {
                    this.finish(t("rsg.gameIDSaved"))
                }
                else {

                    /* The game ID is already saved, only the status did not change */
                    Swal.fire(utils.notification(this.message(answer), "error"))
                    this.setState({ waiting: false })
                    reload()
                }
            }).catch(() => {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
                this.setState({ waiting: false })
                reload()
            })

        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.setState({ waiting: false })
        })

    }

    /* Finish Action */
    finish = (message) => {

        /* Fields */
        const { reload } = this.props

        Swal.fire(utils.notification(message, "success"))
        reload()
        this.close()
    }

    /* Draw Table Section — the table the game ID belongs to */
    _table = () => {

        /* Fields */
        const { name, slug, studio } = this.state

        return (
            <div className="rsg--section">

                <div className="rsg--section-head">
                    <div className="rsg--section-title">
                        <TableIcon />
                        {t("rsg.tableName")}
                    </div>
                </div>

                <div className="rsg--card">

                    <div className="rsg--card-name">{name}</div>

                    <div className="rsg--card-text">
                        {slug !== "" ? <span>{slug}</span> : null}
                        {studio !== "" ? <span>{studio}</span> : null}
                    </div>

                </div>

            </div>
        )
    }

    /* Draw Game ID Section */
    _gameIDSection = () => {

        /* Fields */
        const { gameID } = this.state

        return (
            <div className="rsg--section">

                <div className="rsg--section-head">
                    <div className="rsg--section-title">
                        <KeyIcon size={13} />
                        {t("rsg.gameID")}
                    </div>
                </div>

                <Input
                    ref={(ref) => this._gameID = ref}
                    label={t("rsg.gameID")}
                    value={gameID}
                    maxLength={GAME_ID_LENGTH}
                    onChange={(event) => this.setState({ gameID: event.target.value })}
                />

                <div className="rsg--hint">
                    <InfoIcon />
                    <span>{t("rsg.gameIDHint")}</span>
                </div>

            </div>
        )
    }

    /* Draw Status Section — a table without a game ID never reaches the report */
    _status = () => {

        /* Fields */
        const { gameID, isActive } = this.state
        const mapped = gameID.trim() !== ""

        /* Check Mapping */
        if (!mapped) {
            return null
        }

        return (
            <div className="rsg--section">

                <div className="rsg--section-head">
                    <div className="rsg--section-title">
                        <SendIcon />
                        {t("rsg.reportState")}
                    </div>
                </div>

                <div className={`rsg--toggle ${isActive ? "active" : ""}`}>

                    <div className="rsg--toggle-text">
                        <div className="rsg--toggle-name">{isActive ? t("rsg.inReport") : t("rsg.notInReport")}</div>
                        <div className="rsg--toggle-note">{isActive ? t("rsg.inReportNote") : t("rsg.notInReportNote")}</div>
                    </div>

                    <Switch
                        uncheckedIcon={false}
                        checkedIcon={false}
                        onColor="#8bc34a"
                        height={22}
                        width={44}
                        checked={isActive}
                        onChange={() => this.setState({ isActive: !isActive })}
                    />

                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, name, waiting } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="rsg--modal small"
                overlayClassName="rsg--overlay"
                closeTimeoutMS={200}
            >

                <div className="rsg--modal-header">

                    <div className="rsg--modal-head-text">
                        <h2>{t("rsg.gameTitle")}</h2>
                        <p>{name}</p>
                    </div>

                    <div onClick={() => this.close()} className="rsg--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                <div className="rsg--modal-body">

                    {/* Draw Table Section */}
                    {this._table()}

                    {/* Draw Game ID Section */}
                    {this._gameIDSection()}

                    {/* Draw Status Section */}
                    {this._status()}

                </div>

                <div className="rsg--modal-footer">

                    <div onClick={() => this.close()} className="rsg--button ghost">{t("common.cancel")}</div>

                    {waiting
                        ? <div className="rsg--button primary"><div className="rsg--loading small" /></div>
                        : <div onClick={() => this.save()} className="rsg--button primary">{t("common.save")}</div>
                    }

                </div>

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(RsgGameForm)
